import { useState, useEffect, useCallback, useMemo } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import InternalBookingCardBase from '../common/InternalBookingCardBase';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import { extractUpgradeSymbols } from '../../../../util/upgradeSymbolsHelper';

const TEAMS = [
  { id: 0, label: 'Orange', color: '#e88935' },
  { id: 1, label: 'Grün', color: '#32a852' },
  { id: 2, label: 'Blau', color: '#4c5ad4' },
  { id: 3, label: 'Pink', color: '#db2acf' },
];

const colorCalc = id => {
  const t = TEAMS.find(team => team.id === id);
  return t ? t.color : '#ffffff';
};
const EMPTY_UPGRADES = [];

function BookingCardPaintballv2({
  booking,
  updateBookingState,
  slotMode,
  handleOpenModal,
  renderAvailableSlots,
  onBookingShift,
  entertainment,
  onDragStart,
  onDragActivated,
  onDragEnd,
  availableShiftTimes,
  isShiftable,
}) {
  const { updateInternalBooking } = useInternalBooking();
  const [teamColor, setTeamColor] = useState('#ffffff');
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const { _id, pbSpecific, participantCount } = booking;
  const playerGroupID = pbSpecific?.playerGroupID;

  const upgradeSymbols = useMemo(() => extractUpgradeSymbols(booking), [booking]);

  useEffect(() => {
    if (playerGroupID !== undefined) {
      setTeamColor(colorCalc(playerGroupID));
    }
  }, [playerGroupID]);

  const handleGreaterThanClick = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const updatePlayerGroupID = useCallback(
    async newId => {
      const update = { pbSpecific: { playerGroupID: newId } };
      const result = await updateInternalBooking(_id, update);
      const resolvedId = result?.pbSpecific?.playerGroupID ?? newId;
      setTeamColor(colorCalc(resolvedId));
      const merged = {
        ...(result || {}),
        pbSpecific: { ...(result?.pbSpecific || {}), playerGroupID: resolvedId },
      };
      updateBookingState(_id, merged);
      handleClose();
    },
    [_id, updateInternalBooking, updateBookingState, handleClose]
  );

  const handleCardClick = useCallback(
    (bookingId, color) => handleOpenModal?.(bookingId, color),
    [handleOpenModal]
  );

  // Paintball header with 4 team rows is taller than Lasertag's; match v1 topOffset
  const topOffsetFn = useMemo(
    () => (slotMode ? bk => 112 + (bk.i - 1) * 45 : bk => 40 + (bk.i - 1) * 45),
    [slotMode]
  );

  const getParticipantCount = useCallback(bk => bk.participantCount, []);

  const iconProps = useMemo(
    () => ({
      upgrades: upgradeSymbols,
      manualUpgrades: EMPTY_UPGRADES,
      participantCount,
      slotMode,
      onGreaterThanClick: handleGreaterThanClick,
      color: teamColor,
    }),
    [upgradeSymbols, participantCount, slotMode, handleGreaterThanClick, teamColor]
  );

  return (
    <InternalBookingCardBase
      booking={booking}
      color={teamColor}
      onCardClick={handleCardClick}
      onBookingShift={onBookingShift}
      entertainment={entertainment}
      onDragStart={onDragStart}
      onDragActivated={onDragActivated}
      onDragEnd={onDragEnd}
      availableShiftTimes={availableShiftTimes}
      isShiftable={isShiftable}
      renderAvailableSlots={renderAvailableSlots}
      topOffset={topOffsetFn}
      getParticipantCount={getParticipantCount}
      iconProps={iconProps}
    >
      {slotMode && (
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleClose}
          MenuListProps={{ 'aria-labelledby': 'basic-button' }}
          data-ignore-card-click
        >
          {TEAMS.map(team => (
            <MenuItem
              key={team.id}
              sx={{ color: team.color, fontWeight: 'bold' }}
              data-ignore-card-click
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                updatePlayerGroupID(team.id);
              }}
            >
              {team.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </InternalBookingCardBase>
  );
}

export default BookingCardPaintballv2;
